import { commaListSchema } from "@birdwatch/common";
import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { z } from "zod";

import { ScheduleMain } from "@/features/office/schedule";
import { ConnectGoogleCalendarProvider } from "@/features/office/schedule/_providers/connect-google-calendar-provider";
import { AvailabilityMode } from "@/features/office/schedule/_types/availability-mode";
import { GroupByOption } from "@/features/office/schedule/_types/group-option";
import { HourGridDensity } from "@/features/office/schedule/_types/hour-grid-density";
import { ScheduleDuration } from "@/features/office/schedule/_types/schedule-duration";
import { ScheduleView } from "@/features/office/schedule/_types/schedule-view";
import { AsideTab } from "@/features/office/schedule/aside/_types/aside-tab";
import { VisitTab } from "@/features/office/schedule/aside/event/visit/_types/visit-tab";
import {
  filterSearchFragment,
  notificationSearchFragment,
  SortDirection,
  withOperators,
} from "@/features/shared/filters-new";
import { maskSearchFragment } from "@/features/shared/route-masking";

const defaultValues = {
  layout: ScheduleView.CALENDAR,
  display: ScheduleDuration.WEEK,
  groupBy: GroupByOption.DAY,
  density: HourGridDensity.COMFORTABLE,
  weekends: true,
  visitTab: VisitTab.OVERVIEW,
  ordering: SortDirection.ASCENDING,
};

const queryParamsSchema = z.object({
  search: z.string().optional(),
  savedView: z.string().optional(),
  date: z.string().optional(),
  layout: z.enum(ScheduleView).default(defaultValues.layout).optional(),
  display: z.enum(ScheduleDuration).default(defaultValues.display).optional(),
  groupBy: z.enum(GroupByOption).default(defaultValues.groupBy).optional(),
  density: z.enum(HourGridDensity).default(defaultValues.density).optional(),
  weekends: z.coerce.boolean().default(defaultValues.weekends).optional(),
  tab: z.enum(AsideTab).optional(),
  userIds: commaListSchema,
  inactiveUserIds: commaListSchema,
  hiddenUserIds: commaListSchema,
  appointment: z.string().optional(),
  event: z.string().optional(),
  ooo: z.string().optional(),
  visit: z.string().optional(),
  visitTab: z.enum(VisitTab).default(defaultValues.visitTab).optional(),
  expense: z.string().optional(),
  report: z.string().optional(),
  connectGGCalendarStatus: z.string().optional(),
  ordering: z.enum(SortDirection).default(defaultValues.ordering).optional(),
  availability: z.enum(AvailabilityMode).optional(),
  filters: z
    .object({
      types: withOperators(commaListSchema),
      statuses: withOperators(commaListSchema),
      memberIds: withOperators(commaListSchema),
      managerIds: withOperators(commaListSchema),
      placeIds: withOperators(commaListSchema),
      locationIds: withOperators(commaListSchema),
      ...notificationSearchFragment,
    })
    .optional(),
  ...maskSearchFragment,
  ...filterSearchFragment,
});

export const routeId = "/office/schedule/";

export const Route = createFileRoute("/office/schedule/")({
  component: () => (
    <ConnectGoogleCalendarProvider>
      <ScheduleMain />
    </ConnectGoogleCalendarProvider>
  ),
  validateSearch: queryParamsSchema,
  search: {
    middlewares: [stripSearchParams(defaultValues)],
  },
});
